const API_URL = import.meta.env.PROD ? '/api/chatbot' : 'http://localhost:3003/api/chatbot';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface ChatbotConversation {
  id?: number;
  session_id: string;
  language?: string;
  customer_name?: string;
  country?: string;
  email?: string;
  phone?: string;
  check_in?: string;
  check_out?: string;
  messages: ChatMessage[];
  status: 'active' | 'completed' | 'abandoned';
  created_at?: string;
  updated_at?: string;
}

export const chatbotAPI = {
  // Get all conversations
  async getConversations(): Promise<ChatbotConversation[]> {
    const response = await fetch(`${API_URL}/conversations`);
    if (!response.ok) throw new Error('Failed to fetch conversations');
    return response.json();
  },

  // Get single conversation
  async getConversation(sessionId: string): Promise<ChatbotConversation | null> {
    const response = await fetch(`${API_URL}/conversations/${sessionId}`);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch conversation');
    return response.json();
  },

  // Create new conversation
  async createConversation(data: Omit<ChatbotConversation, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; id: number; session_id: string }> {
    const response = await fetch(`${API_URL}/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create conversation');
    return response.json();
  },

  // Update conversation
  async updateConversation(sessionId: string, updates: Partial<ChatbotConversation>): Promise<void> {
    const response = await fetch(`${API_URL}/conversations/${sessionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error('Failed to update conversation');
  },

  // Add message to conversation
  async addMessage(sessionId: string, message: ChatMessage): Promise<void> {
    const response = await fetch(`${API_URL}/conversations/${sessionId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(message)
    });
    if (!response.ok) throw new Error('Failed to add message');
  },

  // Delete conversation
  async deleteConversation(sessionId: string): Promise<void> {
    const response = await fetch(`${API_URL}/conversations/${sessionId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete conversation');
  }
};
